import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './MyLibrary.module.scss';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { useAuthModal } from '../../../providers/auth-modal.provider';
import { fetchSavedVideos, fetchViewingHistory } from '../../../services/library.service';
import { fetchPurchasedVideos } from '../../../services/purchase.service';
import { buildVideoCardData } from '../../../utils/video-card';
import VideoCard from '../../VideoCard/VideoCard';
import VideoCardSkeleton from '../../VideoCard/VideoCardSkeleton';
import PageHeader from '../../PageHeader/PageHeader';
import SearchDiscoveryPanels, { SearchDiscoverySkeleton } from '../../Search/SearchDiscoveryPanels';
import type { Video } from '../../../types/video.types';
import type { VideoCardData } from '../../../types/video-card.types';
import { useSavedStatus } from '../../../providers/saved-status.provider';
import type { ViewingHistoryItemResponse } from '../../../types/library.types';
import { Routes } from '../../../types/constants';
import {
    buildLibraryDiscoveryMetadata,
    filterLibraryVideos
} from '../../../utils/library-discovery';
import type { LibraryDiscoveryMetadata } from '../../../utils/library-discovery';
import { useContinueWatching } from '../../../hooks/useContinueWatching';
import { useSuggestedVideos } from '../../../hooks/useSuggestedVideos';
import WatchMomentumPanel from '../../WatchMomentum/WatchMomentumPanel';
import { usePurchaseStatus } from '../../../providers/purchase-status.provider';

type LibraryTab = 'saved' | 'purchases' | 'history';
const HISTORY_PAGE_SIZE = 20;
const HISTORY_RESTART_THRESHOLD_PERCENT = 95;

const formatDateTime = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleString();
};

const formatSeconds = (value?: number | null) => {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return '0:00';
    }

    const rounded = Math.max(0, Math.floor(value));
    const minutes = Math.floor(rounded / 60);
    const seconds = rounded % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const MyLibrary = () => {
    const { getUserInfo, isLoggedIn, isUserStateLoading } = useUserState();
    const { openModal } = useAuthModal();
    const { savedVideoIds } = useSavedStatus();
    const { ownedVideoIds } = usePurchaseStatus();
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const activeTab = (tabParam === 'purchases' || tabParam === 'history' ? tabParam : 'saved') as LibraryTab;
    const userToken = getUserInfo()?.token ?? null;
    const userTokenRef = useRef(userToken);
    const [libraryAuthKey, setLibraryAuthKey] = useState(userToken);
    // Async library requests use this current-session key to discard old-account responses.
    userTokenRef.current = userToken;

    const [savedVideos, setSavedVideos] = useState<Video[]>([]);
    const [savedUnavailableCount, setSavedUnavailableCount] = useState(0);
    const [savedLoading, setSavedLoading] = useState(false);
    const [savedError, setSavedError] = useState<string | null>(null);
    const [savedSearch, setSavedSearch] = useState('');
    const [savedLoaded, setSavedLoaded] = useState(false);

    const [purchasedVideos, setPurchasedVideos] = useState<Video[]>([]);
    const [purchasesLoading, setPurchasesLoading] = useState(false);
    const [purchasesError, setPurchasesError] = useState<string | null>(null);
    const [purchasesSearch, setPurchasesSearch] = useState('');
    const [purchasesLoaded, setPurchasesLoaded] = useState(false);
    const purchaseEntitlementsKey = Array.from(ownedVideoIds).sort().join('|');
    const [loadedPurchaseEntitlementsKey, setLoadedPurchaseEntitlementsKey] = useState<string | null>(null);

    const [historyItems, setHistoryItems] = useState<ViewingHistoryItemResponse[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState<string | null>(null);
    const [historySearch, setHistorySearch] = useState('');
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [historyPageIndex, setHistoryPageIndex] = useState(0);
    const [historyTotalPages, setHistoryTotalPages] = useState(0);
    const hasCurrentLibraryState = libraryAuthKey === userToken;
    const currentSavedVideos = useMemo(
        () => hasCurrentLibraryState ? savedVideos : [],
        [hasCurrentLibraryState, savedVideos]
    );
    const currentSavedUnavailableCount = hasCurrentLibraryState ? savedUnavailableCount : 0;
    const currentSavedLoading = !hasCurrentLibraryState || savedLoading;
    const currentSavedError = hasCurrentLibraryState ? savedError : null;
    const currentPurchasedVideos = useMemo(
        () => hasCurrentLibraryState ? purchasedVideos : [],
        [hasCurrentLibraryState, purchasedVideos]
    );
    const currentPurchasesLoading = !hasCurrentLibraryState || purchasesLoading;
    const currentPurchasesError = hasCurrentLibraryState ? purchasesError : null;
    const currentHistoryItems = useMemo(
        () => hasCurrentLibraryState ? historyItems : [],
        [hasCurrentLibraryState, historyItems]
    );
    const currentHistoryLoading = !hasCurrentLibraryState || historyLoading;
    const currentHistoryError = hasCurrentLibraryState ? historyError : null;
    const currentHistoryPageIndex = hasCurrentLibraryState ? historyPageIndex : 0;
    const currentHistoryTotalPages = hasCurrentLibraryState ? historyTotalPages : 0;

    useEffect(() => {
        setLibraryAuthKey(userToken);
        setSavedVideos([]);
        setSavedUnavailableCount(0);
        setSavedLoaded(false);
        setSavedLoading(false);
        setSavedError(null);
        setPurchasesLoaded(false);
        setPurchasesLoading(false);
        setPurchasesError(null);
        setLoadedPurchaseEntitlementsKey(null);
        setPurchasedVideos([]);
        setHistoryItems([]);
        setHistoryLoaded(false);
        setHistoryLoading(false);
        setHistoryError(null);
        setHistoryPageIndex(0);
        setHistoryTotalPages(0);
    }, [userToken]);

    const handleTabChange = (tab: LibraryTab) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('tab', tab);
        setSearchParams(nextParams, { replace: true });
    };

    const loadSaved = useCallback(async () => {
        const userInfo = getUserInfo();
        if (!userInfo?.token) {
            return;
        }

        const requestedAuthKey = userInfo.token;
        setSavedLoading(true);
        setSavedError(null);
        try {
            const response = await fetchSavedVideos(requestedAuthKey);
            if (userTokenRef.current !== requestedAuthKey) return;
            setSavedVideos(response.Items ?? []);
            setSavedUnavailableCount(response.UnavailableCount ?? 0);
            setSavedLoaded(true);
        } catch {
            if (userTokenRef.current !== requestedAuthKey) return;
            setSavedError('Unable to load your saved videos. Please try again soon.');
        } finally {
            if (userTokenRef.current === requestedAuthKey) {
                setSavedLoading(false);
            }
        }
    }, [getUserInfo]);

    const loadPurchases = useCallback(async () => {
        const userInfo = getUserInfo();
        if (!userInfo?.token) {
            return;
        }

        const requestedAuthKey = userInfo.token;
        setPurchasesLoading(true);
        setPurchasesError(null);
        try {
            const response = await fetchPurchasedVideos(requestedAuthKey);
            if (userTokenRef.current !== requestedAuthKey) return;
            setPurchasedVideos(response);
            setPurchasesLoaded(true);
            setLoadedPurchaseEntitlementsKey(purchaseEntitlementsKey);
        } catch {
            if (userTokenRef.current !== requestedAuthKey) return;
            setPurchasesError('Unable to load your purchases. Please try again soon.');
        } finally {
            if (userTokenRef.current === requestedAuthKey) {
                setPurchasesLoading(false);
            }
        }
    }, [getUserInfo, purchaseEntitlementsKey]);

    const loadHistory = useCallback(async (page: number, append: boolean) => {
        const userInfo = getUserInfo();
        if (!userInfo?.token) {
            return;
        }

        const requestedAuthKey = userInfo.token;
        setHistoryLoading(true);
        if (!append) {
            setHistoryError(null);
        }

        try {
            const response = await fetchViewingHistory(requestedAuthKey, page, HISTORY_PAGE_SIZE);
            if (userTokenRef.current !== requestedAuthKey) return;
            const nextItems = response.Results ?? [];
            setHistoryItems((current) => append ? [...current, ...nextItems] : nextItems);
            setHistoryPageIndex(response.PageIndex ?? page);
            setHistoryTotalPages(response.TotalPages ?? 0);
            setHistoryLoaded(true);
        } catch {
            if (userTokenRef.current !== requestedAuthKey) return;
            setHistoryError('Unable to load your viewing history. Please try again soon.');
        } finally {
            if (userTokenRef.current === requestedAuthKey) {
                setHistoryLoading(false);
            }
        }
    }, [getUserInfo]);

    useEffect(() => {
        if (isUserStateLoading || !isLoggedIn) {
            return;
        }

        if (activeTab === 'saved' && !savedLoaded && !savedLoading) {
            void loadSaved();
        }

        if (activeTab === 'purchases'
            && (!purchasesLoaded || loadedPurchaseEntitlementsKey !== purchaseEntitlementsKey)
            && !purchasesLoading) {
            void loadPurchases();
        }

        if (activeTab === 'history' && !historyLoaded && !historyLoading) {
            void loadHistory(1, false);
        }
    }, [
        activeTab,
        historyLoaded,
        historyLoading,
        isLoggedIn,
        isUserStateLoading,
        loadHistory,
        loadPurchases,
        loadedPurchaseEntitlementsKey,
        purchaseEntitlementsKey,
        loadSaved,
        purchasesLoaded,
        purchasesLoading,
        savedLoaded,
        savedLoading
    ]);

    useEffect(() => {
        if (!isLoggedIn || activeTab !== 'saved' || !savedLoaded || savedLoading) {
            return;
        }

        const expectedCount = savedVideoIds.size;
        if (expectedCount === 0) {
            if (savedVideos.length > 0 || savedUnavailableCount > 0) {
                setSavedVideos([]);
                setSavedUnavailableCount(0);
            }
            return;
        }

        if (savedVideos.length + savedUnavailableCount !== expectedCount) {
            void loadSaved();
        }
    }, [
        activeTab,
        isLoggedIn,
        loadSaved,
        savedLoaded,
        savedLoading,
        savedUnavailableCount,
        savedVideoIds,
        savedVideos.length
    ]);

    const savedLibraryVideos = useMemo(
        () => currentSavedVideos.filter((video) => savedVideoIds.has(video.Id)),
        [currentSavedVideos, savedVideoIds]
    );

    const savedDiscoveryMetadata = useMemo(
        () => buildLibraryDiscoveryMetadata(savedLibraryVideos),
        [savedLibraryVideos]
    );

    const savedCards = useMemo<VideoCardData[]>(() => (
        filterLibraryVideos(savedLibraryVideos, savedSearch).map(buildVideoCardData)
    ), [savedLibraryVideos, savedSearch]);

    const purchaseDiscoveryMetadata = useMemo(
        () => buildLibraryDiscoveryMetadata(currentPurchasedVideos),
        [currentPurchasedVideos]
    );

    const purchaseCards = useMemo<VideoCardData[]>(() => {
        return filterLibraryVideos(currentPurchasedVideos, purchasesSearch).map(buildVideoCardData);
    }, [currentPurchasedVideos, purchasesSearch]);

    const historyVideos = useMemo(
        () => currentHistoryItems.map((item) => item.Video as Video),
        [currentHistoryItems]
    );

    const historyDiscoveryMetadata = useMemo(
        () => buildLibraryDiscoveryMetadata(historyVideos),
        [historyVideos]
    );
    const activeLibraryVideos = useMemo(() => {
        if (activeTab === 'purchases') {
            return currentPurchasedVideos;
        }

        if (activeTab === 'history') {
            return historyVideos;
        }

        return savedLibraryVideos;
    }, [activeTab, currentPurchasedVideos, historyVideos, savedLibraryVideos]);
    const continueWatching = useContinueWatching({ limit: 4 });
    const librarySuggestedVideos = useSuggestedVideos({
        sourceVideo: activeLibraryVideos[0],
        limit: 4,
        excludeVideoUrls: continueWatching.items.map((item) => item.video.Url)
    });

    const historyEntries = useMemo(() => {
        const filtered = filterLibraryVideos(historyVideos, historySearch);
        const filteredIds = new Set(filtered.map((video) => video.Id));

        return currentHistoryItems
            .filter((item) => filteredIds.has(item.Video.Id))
            .map((item) => {
                const safeResume = Math.max(0, Math.floor(item.ResumeSeconds ?? 0));
                const resumeSeconds = item.ProgressPercent >= HISTORY_RESTART_THRESHOLD_PERCENT ? 0 : safeResume;
                const encodedShortId = encodeURIComponent(item.Video.Url);
                const watchUrl = `${Routes.WATCH}?v=${encodedShortId}&t=${resumeSeconds}`;

                const card = buildVideoCardData(item.Video as Video);
                card.watchUrl = watchUrl;

                return {
                    item,
                    card,
                    resumeSeconds
                };
            });
    }, [currentHistoryItems, historySearch, historyVideos]);

    const handleOpenLogin = () => {
        openModal('sign_in', window.location.href);
    };
    const hasLibraryMomentum = continueWatching.isLoading
        || continueWatching.items.length > 0
        || librarySuggestedVideos.isLoading
        || librarySuggestedVideos.videos.length > 0;
    const libraryMomentumPanel = hasLibraryMomentum ? (
        <WatchMomentumPanel
            variant="sidebar"
            continueItems={continueWatching.items}
            suggestedVideos={librarySuggestedVideos.videos}
            isContinueLoading={continueWatching.isLoading}
            isSuggestedLoading={librarySuggestedVideos.isLoading}
            continueTitle={activeTab === 'history' ? 'Resume Watching' : 'Continue Watching'}
            continueSubtitle="Jump back into recent fights."
            suggestedTitle="Suggested For Your Library"
            suggestedSubtitle="More videos connected to this tab."
        />
    ) : null;

    const hasDiscoveryMetadata = (metadata: LibraryDiscoveryMetadata) => (
        metadata.tags.length > 0 || metadata.fighters.length > 0 || metadata.events.length > 0
    );

    const renderDiscoveryPanel = (
        title: string,
        metadata: LibraryDiscoveryMetadata,
        onSearchChange: (value: string) => void,
        showSkeleton: boolean
    ) => {
        if (showSkeleton) {
            return (
                <aside className={styles.discoveryPanel} aria-label={title}>
                    <h2 className={styles.discoveryTitle}>{title}</h2>
                    <SearchDiscoverySkeleton />
                </aside>
            );
        }

        if (!hasDiscoveryMetadata(metadata)) {
            return null;
        }

        return (
            <aside className={styles.discoveryPanel} aria-label={title}>
                <h2 className={styles.discoveryTitle}>{title}</h2>
                <SearchDiscoveryPanels
                    tags={metadata.tags}
                    fighters={metadata.fighters}
                    events={metadata.events}
                    showVideos={false}
                    showViewAll={false}
                    onTagSelect={(tag) => onSearchChange(`#${tag.name}`)}
                    onFighterSelect={onSearchChange}
                    onEventSelect={onSearchChange}
                />
            </aside>
        );
    };

    const renderActiveFilter = (value: string, onClear: () => void) => {
        const label = value.trim();
        if (!label) {
            return null;
        }

        return (
            <div className={styles.activeFilterRow}>
                <span>
                    Filtered by <strong>{label}</strong>
                </span>
                <button type="button" onClick={onClear}>
                    Clear
                </button>
            </div>
        );
    };

    const renderPanel = (
        {
            title,
            subtitle,
            discoveryTitle,
            discoveryMetadata,
            searchValue,
            onSearchChange,
            isLoading,
            errorMessage,
            cards,
            emptyTitle,
            emptyCopy,
            unavailableCount,
            railBefore
        }: {
            title: string;
            subtitle: string;
            discoveryTitle: string;
            discoveryMetadata: LibraryDiscoveryMetadata;
            searchValue: string;
            onSearchChange: (value: string) => void;
            isLoading: boolean;
            errorMessage: string | null;
            cards: VideoCardData[];
            emptyTitle: string;
            emptyCopy: string;
            unavailableCount?: number;
            railBefore?: React.ReactNode;
        }
    ) => {
        const discoveryPanel = renderDiscoveryPanel(
            discoveryTitle,
            discoveryMetadata,
            onSearchChange,
            isLoading && cards.length === 0
        );
        const hasSideRail = Boolean(railBefore || discoveryPanel);
        const workspaceClassName = [
            styles.libraryWorkspace,
            hasSideRail ? styles.libraryWorkspaceWithDiscovery : ''
        ].filter(Boolean).join(' ');

        return (
            <>
                <div className={styles.toolbar}>
                    <div>
                        <h2 className={styles.sectionTitle}>{title}</h2>
                        <p className={styles.sectionSubtitle}>{subtitle}</p>
                    </div>
                </div>

                {renderActiveFilter(searchValue, () => onSearchChange(''))}

                {typeof unavailableCount === 'number' && unavailableCount > 0 ? (
                    <div className={styles.notice}>
                        {unavailableCount === 1
                            ? '1 saved video is no longer available.'
                            : `${unavailableCount} saved videos are no longer available.`}
                    </div>
                ) : null}

                <div className={workspaceClassName}>
                    {hasSideRail ? (
                        <aside className={styles.librarySideRail} aria-label={`${title} discovery`}>
                            {railBefore}
                            {discoveryPanel}
                        </aside>
                    ) : null}

                    <div className={styles.libraryMain}>
                        {errorMessage ? (
                            <div className={styles.stateCard}>
                                <h2>We hit a snag</h2>
                                <p>{errorMessage}</p>
                            </div>
                        ) : null}

                        {isLoading ? (
                            <div className={styles.grid} aria-busy="true">
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <div key={`library-skeleton-${index}`} className={styles.cardCell}>
                                        <VideoCardSkeleton />
                                    </div>
                                ))}
                            </div>
                        ) : null}

                        {!isLoading && cards.length === 0 ? (
                            <div className={styles.stateCard}>
                                <h2>{emptyTitle}</h2>
                                <p>{emptyCopy}</p>
                            </div>
                        ) : null}

                        {!isLoading && cards.length > 0 ? (
                            <div className={styles.grid}>
                                {cards.map((video) => (
                                    <div key={video.id} className={styles.cardCell}>
                                        <VideoCard video={video} />
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>
            </>
        );
    };

    const historyDiscoveryPanel = isLoggedIn && activeTab === 'history'
        ? renderDiscoveryPanel(
            'Explore history',
            historyDiscoveryMetadata,
            setHistorySearch,
            currentHistoryLoading && historyEntries.length === 0
        )
        : null;
    const historySideRail = historyDiscoveryPanel || libraryMomentumPanel ? (
        <aside className={styles.librarySideRail} aria-label="History discovery">
            {libraryMomentumPanel}
            {historyDiscoveryPanel}
        </aside>
    ) : null;
    const historyWorkspaceClassName = [
        styles.libraryWorkspace,
        historySideRail ? styles.libraryWorkspaceWithDiscovery : ''
    ].filter(Boolean).join(' ');

    return (
        <main className={styles.page}>
            <PageHeader
                title="My Library"
                subtitle="Save fights for later and revisit everything you have unlocked in one place."
            />

            <section className={styles.section}>
                <div className={styles.tabs}>
                    <button
                        type="button"
                        className={activeTab === 'saved' ? styles.activeTab : styles.tab}
                        onClick={() => handleTabChange('saved')}
                    >
                        Saved
                    </button>
                    <button
                        type="button"
                        className={activeTab === 'purchases' ? styles.activeTab : styles.tab}
                        onClick={() => handleTabChange('purchases')}
                    >
                        My Purchases
                    </button>
                    <button
                        type="button"
                        className={activeTab === 'history' ? styles.activeTab : styles.tab}
                        onClick={() => handleTabChange('history')}
                    >
                        History
                    </button>
                </div>

                {!isLoggedIn && !isUserStateLoading ? (
                    <div className={styles.stateCard}>
                        <h2>Sign in to view your library</h2>
                        <p>Save videos, manage purchases, and jump straight back into the action.</p>
                        <button type="button" className={styles.primaryButton} onClick={handleOpenLogin}>
                            Sign in
                        </button>
                    </div>
                ) : null}

                {isLoggedIn && activeTab === 'saved'
                    ? renderPanel({
                        title: 'Saved videos',
                        subtitle: 'Keep a shortlist of fights you want to watch next.',
                        discoveryTitle: 'Explore saved videos',
                        discoveryMetadata: savedDiscoveryMetadata,
                        searchValue: savedSearch,
                        onSearchChange: setSavedSearch,
                        isLoading: currentSavedLoading,
                        errorMessage: currentSavedError,
                        cards: savedCards,
                        emptyTitle: 'No saved videos yet',
                        emptyCopy: 'Tap the bookmark icon on any video to save it here.',
                        unavailableCount: currentSavedUnavailableCount,
                        railBefore: libraryMomentumPanel
                    })
                    : null}

                {isLoggedIn && activeTab === 'purchases'
                    ? renderPanel({
                        title: 'Your purchases',
                        subtitle: 'All the fights you have unlocked with a one-time purchase.',
                        discoveryTitle: 'Explore purchases',
                        discoveryMetadata: purchaseDiscoveryMetadata,
                        searchValue: purchasesSearch,
                        onSearchChange: setPurchasesSearch,
                        isLoading: currentPurchasesLoading,
                        errorMessage: currentPurchasesError,
                        cards: purchaseCards,
                        emptyTitle: 'No purchases yet',
                        emptyCopy: 'Once you unlock a video, it will appear here for quick access.',
                        railBefore: libraryMomentumPanel
                    })
                    : null}

                {isLoggedIn && activeTab === 'history'
                    ? (
                        <>
                            <div className={styles.toolbar}>
                                <div>
                                    <h2 className={styles.sectionTitle}>Viewing history</h2>
                                    <p className={styles.sectionSubtitle}>Tap a video to resume from where you left off.</p>
                                </div>
                            </div>

                            {renderActiveFilter(historySearch, () => setHistorySearch(''))}

                            <div className={historyWorkspaceClassName}>
                                {historySideRail}

                                <div className={styles.libraryMain}>
                                    {currentHistoryError ? (
                                        <div className={styles.stateCard}>
                                            <h2>We hit a snag</h2>
                                            <p>{currentHistoryError}</p>
                                        </div>
                                    ) : null}

                                    {currentHistoryLoading && historyEntries.length === 0 ? (
                                        <div className={styles.grid} aria-busy="true">
                                            {Array.from({ length: 6 }).map((_, index) => (
                                                <div key={`library-history-skeleton-${index}`} className={styles.cardCell}>
                                                    <VideoCardSkeleton />
                                                </div>
                                            ))}
                                        </div>
                                    ) : null}

                                    {!currentHistoryLoading && historyEntries.length === 0 ? (
                                        <div className={styles.stateCard}>
                                            <h2>No viewing history yet</h2>
                                            <p>Once you start watching videos, they will appear here in most-recent order.</p>
                                        </div>
                                    ) : null}

                                    {!currentHistoryLoading && historyEntries.length > 0 ? (
                                        <div className={styles.grid}>
                                            {historyEntries.map((entry) => (
                                                <div key={`${entry.item.Video.Id}-${entry.item.LastWatchedAtUtc}`} className={styles.cardCell}>
                                                    <VideoCard video={entry.card} />
                                                    <div className={styles.historyMeta}>
                                                        Last watched {formatDateTime(entry.item.LastWatchedAtUtc)} | {entry.item.ProgressPercent.toFixed(0)}% | Resume at {formatSeconds(entry.resumeSeconds)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : null}

                                    {!currentHistoryLoading && currentHistoryPageIndex < currentHistoryTotalPages ? (
                                        <button
                                            type="button"
                                            className={styles.primaryButton}
                                            onClick={() => {
                                                if (!currentHistoryLoading) {
                                                    void loadHistory(currentHistoryPageIndex + 1, true);
                                                }
                                            }}
                                        >
                                            Load more
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        </>
                    ) : null}
            </section>
        </main>
    );
};

export default MyLibrary;
